import type { ConnectionState } from '@/hooks/useDashboard';

export type ViewName = 'overview' | 'incidents' | 'hosts' | 'bloomberg';

interface Props {
  view: ViewName;
  onViewChange: (view: ViewName) => void;
  openIncidentCount: number;
  connection: ConnectionState;
  updatedAt: Date | null;
  error: string | null;
  focusMode: boolean;
  onFocusToggle: () => void;
  onRefresh: () => void;
}

const TABS: Array<{ id: ViewName; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'incidents', label: 'Incidents' },
  { id: 'hosts', label: 'Hosts' },
  { id: 'bloomberg', label: 'Bloomberg' },
];

export function TopBar({
  view,
  onViewChange,
  openIncidentCount,
  connection,
  updatedAt,
  error,
  focusMode,
  onFocusToggle,
  onRefresh,
}: Props) {
  const connectionClass =
    connection === 'live' ? 'is-live' : connection === 'error' ? 'is-stale' : '';

  return (
    <header className="topbar">
      <div className="topbar__brand">
        <span className={`conn ${connectionClass}`} title={`Connection: ${connection}`}>
          <i />
        </span>
        <span className="topbar__title">Ops Monitor</span>
      </div>

      <nav className="tabs" role="tablist" aria-label="Views">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={view === tab.id}
            className={`tab${view === tab.id ? ' is-active' : ''}`}
            onClick={() => onViewChange(tab.id)}
          >
            {tab.label}
            {tab.id === 'incidents' && openIncidentCount > 0 && (
              <span className="tab__count">{openIncidentCount}</span>
            )}
          </button>
        ))}
      </nav>

      <div className="topbar__right">
        <button
          className="iconbtn"
          aria-pressed={focusMode}
          title="Focus mode — hide healthy services"
          onClick={onFocusToggle}
        >
          Focus
        </button>
        <button className="iconbtn" title="Refresh now" onClick={onRefresh}>
          Refresh
        </button>
        <span className="topbar__clock" aria-live="polite">
          {error
            ? `disconnected · ${error}`
            : updatedAt
              ? `updated ${updatedAt.toLocaleTimeString()}`
              : 'connecting…'}
        </span>
      </div>
    </header>
  );
}
